import { Injectable, inject } from '@angular/core'
import { Store, Select } from '@ngxs/store'
import {
  Login,
  LoginFromStorage,
  Logout,
  ForgotPassword,
  FetchUserInfos,
  SetToken,
  UpdatePasswordWithToken,
  UpdatePassword,
} from './auth.actions'
import {
  LoginMutationVariables,
  ForgotPasswordMutationVariables,
  UpdatePasswordWithTokenMutationVariables,
} from '../graphql'
import { Dispatch } from '@ngxs-labs/dispatch-decorator'
import { AuthState, AuthStateModel } from './auth.state'
import {
  defer,
  filter,
  firstValueFrom,
  map,
  Observable,
  of,
  race,
  switchMap,
  take,
  timeout,
  timer,
} from 'rxjs'
import { DecodedToken } from '@wm-front/shared/interfaces'
import { CreatePasswordError } from './enums'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { CountryCode } from 'libphonenumber-js'
import { DatadogService, FeatureFlagService } from '@wm-front/core-tools'
import { getDatadogUser, getFeatureFlagUser } from '@wm-front/shared/utils'
import { UpdatePasswordParams } from './interfaces'
import { ClerkService } from '@wm-front/clerk'
import { Environment, ENVIRONMENT } from '@wm-front/shared/environment'
import { I18nService } from '@wm-front/core'
import { frFR, enGB, ptPT, zhCN } from '@clerk/localizations'

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AuthFacadeService {
  /** Max time we wait for the Clerk session to be bridged into the store */
  private static readonly TOKEN_BRIDGE_TIMEOUT_MS = 10000

  /** How often the bridged Clerk token is re-written into the store. Clerk
   * session JWTs live 60s and clerk-js refreshes its cache with ~10s leeway,
   * so polling every 25s guarantees the stored token (read by Apollo and by
   * isAuthenticated) is never expired. */
  private static readonly TOKEN_REFRESH_INTERVAL_MS = 25000

  /** Return the authentification state model */
  @Select(AuthState)
  public data$: Observable<AuthStateModel>

  /** Return the accessToken */
  @Select(AuthState.accessToken)
  public accessToken$: Observable<string>

  /** Return the decodedToken */
  @Select(AuthState.decodedToken)
  public decodedToken$: Observable<DecodedToken>

  /** Return the customerId of the current logged in user */
  @Select(AuthState.customerId)
  public customerId$: Observable<number | string>

  /** Return the accountIds of the current logged in user */
  @Select(AuthState.accountIds)
  public accountIds$: Observable<string[] | undefined>

  /** Return the user currency */
  @Select(AuthState.currencyIsoCode)
  public currencyIsoCode$: Observable<string>

  /** Return true if the user is authentificated */
  @Select(AuthState.isAuthenticated)
  public isAuthenticated$: Observable<boolean>

  /** Return the email of the current logged in user */
  @Select(AuthState.email)
  public email$: Observable<string>

  /** Return true if create password meet a problem */
  @Select(AuthState.isCreatePasswordError)
  public isCreatePasswordError$: Observable<CreatePasswordError | null>

  /** Return true if create password is loading */
  @Select(AuthState.isCreatePasswordLoading)
  public isCreatePasswordLoading$: Observable<boolean>

  /** Return whether or not is maximize */
  @Select(AuthState.hasMaximize)
  public hasMaximize$: Observable<boolean>

  /** Return true if not maximize */
  @Select(AuthState.hasNoMaximize)
  public hasNoMaximize$: Observable<boolean>

  /** Return the fullName of the current logged un user */
  @Select(AuthState.fullName)
  public fullName$: Observable<string>

  /** Return the countryCode of the current logged un user */
  @Select(AuthState.countryCode)
  public countryCode$: Observable<CountryCode>

  /** Return true if authenticated has failed */
  @Select(AuthState.errorLogin)
  public errorLogin$: Observable<boolean>

  /** Return true if update password meet a problem */
  @Select(AuthState.isUpdatePasswordError)
  public isUpdatePasswordError$: Observable<Error | null>

  /** Return true if update password is loading */
  @Select(AuthState.isUpdatePasswordLoading)
  public isUpdatePasswordLoading$: Observable<boolean>

  /**
   * Resolves once auth is in a stable state: Clerk has loaded AND, if a Clerk
   * session exists, its token has been bridged into the store. Guards must not
   * sample isAuthenticated$ before this emits, otherwise a signed-in user gets
   * bounced to /auth/login while the session→token bridge is still in flight.
   * Also resolves if clerk-js fails to load, so navigation never hangs.
   */
  public clerkReady$: Observable<boolean> = defer(() => {
    if (!this.env.clerk) return of(true)
    return race(
      this._clerk.session$.pipe(take(1)),
      this._clerk.loadError$.pipe(
        take(1),
        map(() => null)
      )
    ).pipe(
      switchMap((session) => {
        if (!session) return of(true)
        return this.isAuthenticated$.pipe(
          filter(Boolean),
          take(1),
          map(() => true),
          timeout({
            first: AuthFacadeService.TOKEN_BRIDGE_TIMEOUT_MS,
            with: () => of(true),
          })
        )
      }),
      take(1)
    )
  })

  public env = inject<Environment>(ENVIRONMENT)

  protected _store = inject(Store)

  protected _datadogService = inject(DatadogService)

  protected _featureFlagService = inject(FeatureFlagService)

  protected _clerk = inject(ClerkService)

  protected _i18n = inject(I18nService)

  protected _clerkLanguages = {
    'fr-FR': frFR,
    'en-GB': enGB,
    'pt-PT': ptPT,
    'zh-Hant-HK': zhCN,
  }

  constructor() {
    if (this.env.clerk) {
      this._clerk.__init({
        publishableKey: this.env.clerk.publishableKey,
        localization:
          this._clerkLanguages[
            (localStorage.getItem('wm-locale') ||
              this._i18n.language) as keyof typeof this._clerkLanguages
          ] || this._clerkLanguages['en-GB'],
        appearance: {
          elements: {
            cardBox: {
              boxShadow: 'none',
              borderRadius: '4px',
            },
            headerTitle: {
              color: 'var(--wm-color-brand-primary)',
              fontSize: '28px',
              fontFamily: 'SanFranciscoSemiBold',
            },
            socialButtonsBlockButton: {
              height: '48px',
              borderRadius: '4px',
            },
            socialButtonsBlockButtonText: {
              fontSize: '14px',
              letterSpacing: '0.25px',
              fontFamily: 'SanFranciscoMedium',
            },
            dividerText: {
              textTransform: 'uppercase',
            },
            form: {
              gap: '1.5rem',
            },
            formFieldInput: {
              height: '50px',
              paddingTop: '24px',
              paddingBottom: '24px',
              border: '1px solid #ccc',
              fontFamily: 'SanFranciscoRegular',
              fontSize: '14px',
              letterSpacing: '0.13px',
              lineHeight: '20px',
              borderRadius: '4px',
            },
            formButtonPrimary: {
              height: '48px',
              boxShadow: 'none !important',
              borderRadius: '4px',
            },
            button: {
              fontSize: '14px',
              letterSpacing: '0.25px',
              fontFamily: 'SanFranciscoMedium',
              lineHeight: '16px',
            },
            footerActionLink: {
              fontSize: '14px',
              color: 'rgb(33, 33, 33)',
            },
          },
          variables: {
            colorPrimary: '#f1653c',
          },
        },
      })
    }
    // TODO: move to other part of app
    this.initDatadog()
    this.initFeatureFlag()
    // Bridge the Clerk session token into the store and KEEP it fresh:
    // Clerk session JWTs expire after 60s, so a one-shot bridge leaves
    // isAuthenticated (and Apollo's auth header) reading an expired snapshot
    // as soon as clerk-js's own refresh cadence and ours drift apart.
    this._clerk.session$
      .pipe(
        switchMap((session) => {
          if (!session) return of(null)
          return timer(0, AuthFacadeService.TOKEN_REFRESH_INTERVAL_MS).pipe(
            switchMap(async () =>
              session.getToken().catch((error) => {
                this._datadogService.addError(error, {
                  feature: 'clerk-token-bridge',
                })
                return null
              })
            )
          )
        })
      )
      .subscribe((token) => {
        if (token) {
          this.setToken(token, 'clerk')
        }
      })
    this._clerk.loadError$.subscribe((error) => {
      this._datadogService.addError(error, { feature: 'clerk-load' })
    })
  }

  /** Dispatch set token action (used by Clerk session bridge)
   * @param { string } token the JWT to set in the state
   * @param { 'clerk' | 'legacy' } mode the authentication mode
   */
  @Dispatch()
  public setToken(
    token: string,
    mode: 'clerk' | 'legacy' = 'legacy'
  ): SetToken {
    return new SetToken(token, mode)
  }

  /** Dispatch login action */
  @Dispatch()
  public login(payload: LoginMutationVariables): Login {
    return new Login(payload)
  }

  /** Dispatch loginFromStorage action */
  @Dispatch()
  public loginFromStorage(): LoginFromStorage {
    return new LoginFromStorage()
  }

  /** Dispatch logout action */
  @Dispatch()
  public logout(): Logout {
    return new Logout()
  }

  /** Dispatch forgot password without being logged action */
  @Dispatch()
  public forgotPassword(
    payload: ForgotPasswordMutationVariables
  ): ForgotPassword {
    return new ForgotPassword(payload)
  }

  /** Dispatch Fetch user infos action with user token in header */
  @Dispatch()
  public fetchUserInfos(): FetchUserInfos {
    return new FetchUserInfos()
  }

  /** Dispatch update password action */
  @Dispatch()
  public updatePasswordWithToken(
    payload: UpdatePasswordWithTokenMutationVariables
  ): UpdatePasswordWithToken {
    return new UpdatePasswordWithToken(payload)
  }

  /** Dispatch the mutation update password
   * @param { UpdatePasswordParams } params the params given to the graphql query
   */
  @Dispatch()
  public updatePassword(params: UpdatePasswordParams): UpdatePassword {
    return new UpdatePassword(params)
  }

  /**
   * Race-free authentication check for route guards. For Clerk, asks Clerk
   * for a token at evaluation time — clerk-js transparently refreshes an
   * expired one — and re-bridges it into the store, so the answer can never
   * be stale just because the persisted 60s token snapshot expired. Falls
   * back to the stored-token check for legacy/token-link sessions.
   * @return { Observable<boolean> }
   */
  public checkAuth$(): Observable<boolean> {
    return defer(() => {
      if (!this.env.clerk) return this.isAuthenticated$.pipe(take(1))
      return race(
        this._clerk.session$.pipe(take(1)),
        this._clerk.loadError$.pipe(
          take(1),
          map(() => null)
        )
      ).pipe(
        switchMap(async (session) => {
          if (!session) return firstValueFrom(this.isAuthenticated$)
          try {
            const token = await session.getToken()
            if (token) {
              this.setToken(token, 'clerk')
              return true
            }
            return false
          } catch (error) {
            this._datadogService.addError(error, {
              feature: 'clerk-token-bridge',
            })
            return firstValueFrom(this.isAuthenticated$)
          }
        }),
        timeout({
          first: AuthFacadeService.TOKEN_BRIDGE_TIMEOUT_MS,
          with: () => this.isAuthenticated$.pipe(take(1)),
        })
      )
    })
  }

  /** Init datadog user information */
  private initDatadog(): void {
    this.decodedToken$
      .pipe(untilDestroyed(this))
      .subscribe((decodedToken: DecodedToken | null) => {
        this._datadogService.setUser(getDatadogUser(decodedToken))
      })
  }

  /** Init feature flag */
  private initFeatureFlag(): void {
    this.decodedToken$
      .pipe(untilDestroyed(this))
      .subscribe((decodedToken: DecodedToken | null) => {
        this._featureFlagService.setUser(getFeatureFlagUser(decodedToken))
      })
  }
}
